import { Data, FullData } from './data.types';

type Group = 'Expense' | 'Revenue';

const defaultSectionInfo: SectionInfo = {};

interface SectionInfo {
	numeric?: boolean;
	group?: Group;
	groupSelect?: boolean;
	hideRefs?: boolean;
	hideSingleFieldHeaders?: boolean;
	enableAddRemove?: 'NumericRefs' | 'All';
}

interface SectionInfoWithMatch extends SectionInfo {
	match?: RegExp;
}

// this information should really be on the api
const info: {
	[documentType: string]: {
		hideRefs?: boolean;
		sections?: SectionInfoWithMatch[];
	};
	Other: {
		hideRefs?: boolean;
		sections: SectionInfoWithMatch[];
	};
} = {
	'Canadian T1': {
		hideRefs: true,
	},
	'Canadian T2125': {
		hideRefs: true,
		sections: [
			{
				match: /^Part 3[AB].*/,
				numeric: true,
			},
			{
				match: /^Part 3C.*/,
				numeric: true,
				group: 'Expense',
				groupSelect: true,
				enableAddRemove: 'NumericRefs',
			},
			{
				match: /^Part 3D.*/,
				numeric: true,
				group: 'Revenue',
				groupSelect: true,
				enableAddRemove: 'NumericRefs',
			},
			{
				match: /^Part 4.*/,
				numeric: true,
				group: 'Expense',
				groupSelect: true,
				enableAddRemove: 'NumericRefs',
			},
			{
				match: /^Part 5.*/,
				numeric: true,
			},
			{
				match: /^Tax, Refundable, Credits and Payments$/,
				numeric: true,
			},
			{
				match: /^Other Cost Of Goods Sold$/,
				numeric: true,
				group: 'Expense',
				groupSelect: true,
				enableAddRemove: 'All',
			},
		],
	},
	'Profit And Loss': {
		hideRefs: true,
		sections: [
			{
				match: /^Gross Profit$/,
				numeric: true,
				hideSingleFieldHeaders: true,
			},
			{
				match: /^(Other )?Expenses$/,
				numeric: true,
				group: 'Expense',
				groupSelect: true,
				hideSingleFieldHeaders: true,
				enableAddRemove: 'All',
			},
			{
				match: /^(Other )?Income$/,
				numeric: true,
				group: 'Revenue',
				groupSelect: true,
				hideSingleFieldHeaders: true,
				enableAddRemove: 'All',
			},
			{
				match: /.*Expenses$/,
				numeric: true,
				hideSingleFieldHeaders: true,
			},
			{
				match: /.*Income$/,
				numeric: true,
				hideSingleFieldHeaders: true,
			},
			{
				match: /^Cost of Goods Sold$/,
				numeric: true,
				group: 'Expense',
				groupSelect: true,
				enableAddRemove: 'All',
			},
			{
				match: /^Total Cost of Goods Sold$/,
				numeric: true,
				hideSingleFieldHeaders: true,
			},
		],
	},
	Other: {
		sections: [
			{
				match: /.*Income$/,
				numeric: true,
			},
			{
				match: /^Other Expenses$/,
				numeric: true,
				group: 'Expense',
				groupSelect: true,
				enableAddRemove: 'All',
			},
			{
				match: /.*Expenses$/,
				numeric: true,
				group: 'Expense',
			},
			{
				match: /^Other Deductions$/,
				numeric: true,
				group: 'Expense',
				groupSelect: true,
				enableAddRemove: 'All',
			},
			{
				match: /.*Deductions$/,
				numeric: true,
				group: 'Expense',
			},
			{
				match: /^Cost of Goods Sold$/,
				numeric: true,
				group: 'Expense',
				groupSelect: true,
				enableAddRemove: 'All',
			},
			{
				match: /^Other Cost Of Goods$/,
				numeric: true,
				group: 'Expense',
				groupSelect: true,
				enableAddRemove: 'All',
			},
		],
	},
};

export const fillData = (data: Data): FullData => {
	return data.map((doc) => ({
		documentGroup: doc.documentGroup,
		error: doc.error,
		documents: doc.documents.map((document) => {
			const documentInfo = info[document.documentType] ?? info.Other;
			return {
				documentType: document.documentType,
				fileId: document.fileId,

				sections: document.sections.map((section) => {
					const sectionInfo =
						(documentInfo.sections ?? [])
							.concat(info.Other.sections)
							.find((s) => {
								return s.match ? s.match.test(section.sectionName) : true;
							}) ?? defaultSectionInfo;

					return {
						...section,
						numeric: sectionInfo.numeric ?? false,
						group: sectionInfo.group ?? null,
						groupSelect: sectionInfo.groupSelect ?? false,
						hideSingleFieldHeaders: sectionInfo.hideSingleFieldHeaders ?? false,
						enableAddRemove: sectionInfo.enableAddRemove,
					};
				}),

				hideRefs:
					info[document.documentType]?.hideRefs ??
					defaultSectionInfo.hideRefs ??
					false,
			};
		}),
	}));
};
